/**
 * Sidesheet — Reusable slide-from-right panel component.
 * Used across the portal for creation and editing flows (replaces centered modals).
 *
 * Features:
 * - Panel slides from right edge with transform: translateX
 * - Semi-transparent overlay with click-to-close
 * - Close on Escape key (useEffect event listener)
 * - CSS transition 300ms ease for slide-in/slide-out
 * - Width: 440px by default (matching prototype sidesheet); `width` widens it for dense forms
 * - Header with title + close button, scrollable body area
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5
 */

import { useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

interface SidesheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Panel width (CSS length). Defaults to 440px; capped at the viewport width. */
  width?: string;
}

export default function Sidesheet({ open, onClose, title, children, width }: SidesheetProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown]);

  return (
    <div
      data-testid="sidesheet-root"
      style={{
        ...s.wrapper,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* Overlay */}
      <div
        data-testid="sidesheet-overlay"
        onClick={onClose}
        style={{
          ...s.overlay,
          opacity: open ? 1 : 0,
        }}
      />

      {/* Panel */}
      <div
        data-testid="sidesheet-panel"
        style={{
          ...s.panel,
          ...(width ? { width } : {}),
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div style={s.header}>
          <h2 style={s.title}>{title}</h2>
          <button
            data-testid="sidesheet-close"
            onClick={onClose}
            style={s.btnClose}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        {/* Body (scrollable) */}
        <div style={s.body}>{children}</div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    transition: 'opacity 300ms ease',
  },
  panel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '440px',
    maxWidth: '100vw',
    background: 'var(--card, #0D1C2E)',
    borderLeft: '1px solid var(--border)',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 300ms ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--text-1)',
  },
  btnClose: {
    background: 'none',
    border: 'none',
    color: 'var(--text-2)',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
};
